<template>
   <div class="hotel-sales-report-container sub-page-wrapper">
      <Aside />
      <Main>
         <Navbar />
         <div class="container-fluid">
            <div class="row">
               <div class="col gts-box mx-3">
                  <h1 class="sub-page-header">{{ $t("hotelSalesReport_title") }}</h1>
                  <div class="sub-page-row-wrapper row gy-3">
                     <div class="col-12 col-md-6 col-lg-2">
                        <UISelectBox
                           :key="$i18n.locale"
                           :label="$t('hotelSalesReport_filter_lblReportType')"
                           :options="dateTypeOptions"
                           v-model="searchQuery.dateType"
                           light-theme />
                     </div>
                     <div class="col-12 col-md-6 col-lg-2">
                        <UIDatePicker
                           :key="$i18n.locale"
                           :label="$t('hotelSalesReport_filter_lblFromDate')"
                           :min="searchQuery.date1.min"
                           :max="searchQuery.date1.max"
                           v-model="searchQuery.date1.value" />
                     </div>
                     <div class="col-12 col-md-6 col-lg-2">
                        <UIDatePicker
                           :key="$i18n.locale"
                           :label="$t('hotelSalesReport_filter_lblToDate')"
                           :min="searchQuery.date2.min"
                           :max="searchQuery.date2.max"
                           v-model="searchQuery.date2.value" />
                     </div>
                     <div class="col-12 col-md-6 col-lg-2">
                        <UIMultiSelect
                           :key="$i18n.locale"
                           :label="$t('hotelSalesReport_filter_lblStatus')"
                           :options="voucherStatuses"
                           v-model="searchQuery.voucherStatuses"
                           light-theme
                           allow-search />
                     </div>
                  </div>

                  <div class="sub-page-actions-row row">
                     <div class="sub-pages-actions-wrapper col-12">
                        <UIButton className="default" :text="$t('common_list')" @click.native="listHotelSalesReport" />
                     </div>
                  </div>
               </div>
            </div>

            <template v-if="currentTableOptions.rows.length > 0">
               <ReportSummary entityType="hotel" :summary="summary" />
               <section class="smart-table-wrapper">
                  <SmartTable :options="currentTableOptions" />
               </section>
            </template>
         </div>
      </Main>
   </div>
</template>

<script>
import moment from "moment";
import Popup from "@/gts-popup.js";
import Main from "@/components/unitary/Main.vue";
import Aside from "@/components/unitary/Aside.vue";
import { reportService } from "@/service/index.js";
import Navbar from "@/components/unitary/Navbar.vue";
import UIButton from "@/components/unitary/Button.vue";
import UISelectBox from "@/components/unitary/form2/UISelectBox.vue";
import ReportSummary from "@/components/unitary/SalesReportSummary.vue";
import UIDatePicker from "@/components/unitary/form2/UIDatePicker2.vue";
import UIMultiSelect from "@/components/unitary/form2/UIMultiSelect.vue";

export default {
   name: "HotelSalesReport",
   components: {
      Main,
      Aside,
      Navbar,
      UIButton,
      UISelectBox,
      UIDatePicker,
      UIMultiSelect,
      ReportSummary,
   },
   data() {
      return {
         searchQuery: {
            dateType: 2,
            voucherStatuses: [2, 3],
            date1: {
               min: moment().subtract(1, "year").format(this.$constants.dateFormat),
               max: moment().format(this.$constants.dateFormat),
               value: moment().format(this.$constants.dateFormat),
            },
            date2: {
               min: moment().subtract(1, "year").format(this.$constants.dateFormat),
               max: moment().format(this.$constants.dateFormat),
               value: moment().format(this.$constants.dateFormat),
            },
         },
         tableOptions: {
            columns: [],
            rows: [],
            rules: {
               autoMerge: ["orderId"],
               dominantCell: "orderId",
               showOnMobile: [0, 2, 4, 7],
               highlightedColumns: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
            },
            options: {
               header: {
                  title: this.$options.name,
                  viewOptions: {
                     status: true,
                     perPage: 50,
                     selects: [
                        { name: `3 ${this.$t("common_order")}`, value: 3 },
                        { name: `5 ${this.$t("common_order")}`, value: 5 },
                        { name: `25 ${this.$t("common_order")}`, value: 25 },
                        { name: `50 ${this.$t("common_order")}`, value: 50 },
                        { name: `100 ${this.$t("common_order")}`, value: 100 },
                        { name: `${this.$t("common_all")}`, value: null },
                     ],
                  },
                  viewMode: null,
                  saleDateIndex: 9,
               },
               footer: {
                  pagination: {
                     pages: [],
                     activepage: 1,
                  },
               },
            },
         },
         summary: null,
      };
   },
   computed: {
      Direct() {
         return this.$t("hotelSalesReport_tblMain_tdChannelGts");
      },
      Provider() {
         return this.$t("hotelSalesReport_tblMain_tdChannelProvider");
      },
      dateTypeOptions() {
         return this.$GtsDropdownData.EReportHotelDateType(false, false, [1]);
      },
      voucherStatuses() {
         return this.$GtsDropdownData.EHtlVoucherStatus(false, false, [10, 11, 12, 13, 14]);
      },
      tableColumns() {
         return [
            { label: this.$t("hotelSalesReport_tblMain_thOrderId"), status: true, identifier: "orderId" },
            { label: this.$t("hotelSalesReport_tblMain_thVoucher"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thVoucherStatus"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thAgencyName"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thHotelName"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thContactName"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thContactSurname"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thCheckinDate"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thCheckoutDate"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thSaleDateTime"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thAgencyCommission"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thTotal"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thRoomCount"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thGuestCount"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thOptionDaysLeft"), status: true },
            { label: this.$t("hotelSalesReport_tblMain_thOnUsChannel"), status: true },
         ];
      },
      currentTableOptions() {
         return { ...this.tableOptions, columns: this.tableColumns };
      },
   },
   methods: {
      listHotelSalesReport() {
         this.summary = null;
         this.tableOptions.rows = [];

         this.$store.commit("app/setIsProgressGifActive", true);

         let request = {
            dateType: this.searchQuery.dateType,
            statuses: this.searchQuery.voucherStatuses,
            date1: this.searchQuery.date1.value,
            date2: this.searchQuery.date2.value,
         };

         reportService.hotelSalesReport(request).then((res) => {
            if (!res.result.success) {
               res.result.service = this.$options.name;
               Popup.result(res.result);
               return;
            }
            if (res.result.success) {
               this.$store.commit("app/setIsProgressGifActive", false);
               this.summary = res.summary;
               res.vouchers.map((voucher) => {
                  const rowObj = {
                     orderId: {
                        text: voucher.orderId,
                        class: `gts-badge smart-table-badge-1`,
                        url: `/order/detail/${voucher.orderUuid}`,
                     },
                     voucher: {
                        text: `${!voucher.voucher ? "" : voucher.voucher}`,
                        class: "",
                     },
                     voucherStatus: {
                        text: this.$options.filters.EHtlVoucherStatus(voucher.voucherStatus),
                        class: `voucher-badge gts-badge EHtlVoucherStatus-${voucher.voucherStatus}`,
                     },
                     agencyName: voucher.agencyName,
                     hotelName: voucher.hotelName,
                     contactName: voucher.contactName,
                     contactSurname: voucher.contactSurname,
                     checkInDate: {
                        text: this.$options.filters.fullDateFormat(voucher.checkInDate),
                        class: "",
                     },
                     checkOutDate: {
                        text: this.$options.filters.fullDateFormat(voucher.checkOutDate),
                        class: "",
                     },
                     saleDateTime: {
                        text: this.$options.filters.fullDateFormat(voucher.saleDateTime),
                        class: "",
                     },
                     agencyCommission: {
                        text: this.$options.filters.price(voucher.agencyCommission, voucher.currency),
                        class: "price-display",
                     },
                     total: {
                        text: this.$options.filters.price(voucher.total, voucher.currency),
                        class: "price-display",
                     },
                     roomCount: voucher.roomCount,
                     guestCount: voucher.guestCount,
                     optionDaysLeft: voucher.optionDaysLeft,
                     onUsContractChannel: voucher.onUsContractChannel ? this.Direct : this.Provider,
                  };
                  this.tableOptions.rows.push(rowObj);
               });
            }
         });
      },
   },
   watch: {
      "searchQuery.dateType"(newDateType) {
         const baseDate = moment().format(this.$constants.dateFormat);
         const oneYearAgo = moment().subtract(1, "year").format(this.$constants.dateFormat);
         const oneYearAhead = moment().add(1, "year").format(this.$constants.dateFormat);

         this.searchQuery.date1.value = baseDate;
         this.searchQuery.date2.value = baseDate;

         if (newDateType === 2) {
            this.searchQuery.date1.min = oneYearAgo;
            this.searchQuery.date1.max = baseDate;
            this.searchQuery.date2.min = oneYearAgo;
            this.searchQuery.date2.max = baseDate;
         } else if (newDateType === 3 || newDateType === 4) {
            this.searchQuery.date1.min = oneYearAgo;
            this.searchQuery.date1.max = oneYearAhead;
            this.searchQuery.date2.min = oneYearAgo;
            this.searchQuery.date2.max = oneYearAhead;
         }
      },
      "searchQuery.date1.value"(newDate1) {
         if (moment(newDate1, this.$constants.dateFormat).isAfter(moment(this.searchQuery.date2.value, this.$constants.dateFormat))) {
            this.searchQuery.date2.value = newDate1;
         }
      },
      "searchQuery.date2.value"(newDate2) {
         if (moment(newDate2, this.$constants.dateFormat).isBefore(moment(this.searchQuery.date1.value, this.$constants.dateFormat))) {
            this.searchQuery.date1.value = newDate2;
         }
      },
   },
};
</script>

<style lang="scss" scoped>
.page-container {
   .page-inner {
      width: 90%;
      margin: auto;
      .page-form-row {
         display: grid;
         grid-template-columns: repeat(6, 1fr);
         grid-column-gap: 1rem;
      }
      .page-action-row {
         margin-top: 1rem;
         display: flex;
         justify-content: flex-end;
      }
   }
}
</style>
